import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams, createSearchParams } from "react-router-dom";
import { ExternalLink, Trash2, Lock } from "lucide-react";
import { Header } from "../../../components/header";
import { Loading } from "../../../components/loading";
import { EmptyList } from "../../../components/empty-list";
import { Select, SearchBar } from "../../../components";
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../../components/tables";
import { useCreatedSites, useRemoveCreatedSite } from "../hooks";
import { CustomerSelectSearch } from "../../customers/components";
import { useMe } from "../../users/hooks/useMe";
import { cleanObject } from "../../../helpers/clean-object";
import { formatDisplayDate } from "../../../helpers/date";
import { CreatedSiteStatus, UserRole } from "../../../__generated__/globalTypes";
import { createdSiteStatusLabel } from "../../../helpers/enum-labels";

interface ISearchForm {
  customerId?: string;
  status?: string;
}

const STATUS_STYLES: Record<CreatedSiteStatus, string> = {
  [CreatedSiteStatus.Creating]: "bg-amber-100 text-amber-700",
  [CreatedSiteStatus.Done]: "bg-green-100 text-green-700",
  [CreatedSiteStatus.Error]: "bg-red-100 text-red-700",
};

export const CreatedSitesList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: meData } = useMe();
  const { removeCreatedSite } = useRemoveCreatedSite();
  const isAdmin = meData?.me?.role === UserRole.Admin;

  const form = useForm<ISearchForm>({
    mode: "all",
    defaultValues: {
      customerId: searchParams.get("customerId") ?? "",
      status: searchParams.get("status") ?? "",
    },
  });

  const onSubmit = () => {
    const values = form.getValues();
    navigate({
      pathname: "/created-sites",
      search: `?${createSearchParams(cleanObject(values))}`,
    });
  };

  const customerId = searchParams.get("customerId");
  const status = searchParams.get("status");
  const where: any = {};
  if (customerId) where.customerId = +customerId;
  if (status) where.status = status;

  const { data, loading } = useCreatedSites({ where });

  const handleRemove = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer ce site généré ? Cette action est irréversible.")) return;
    removeCreatedSite(id);
  };

  return (
    <>
      <Header
        title="Sites générés"
        subtitle="Historique des générations de sites de prospection"
      />

      <div className="main-container">
        <SearchBar
          form={form}
          onSubmit={onSubmit}
          placeholder=""
          advancedFilters={
            <>
              <CustomerSelectSearch form={form} name="customerId" />
              <Select label="Statut" className="w-full" {...form.register("status")}>
                <option value="">Tous</option>
                {Object.keys(CreatedSiteStatus).map((key) => (
                  <option key={key} value={key}>
                    {createdSiteStatusLabel(key)}
                  </option>
                ))}
              </Select>
            </>
          }
        />

        <div className="p-4 mb-6 bg-white shadow rounded overflow-x-auto">
          {loading ? (
            <Loading />
          ) : !data?.createdSites?.results?.length ? (
            <EmptyList text="Aucun site généré" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow hover={false}>
                  <TableCell header>Date</TableCell>
                  <TableCell header>Client</TableCell>
                  <TableCell header>Thème</TableCell>
                  <TableCell header align="center">
                    Statut
                  </TableCell>
                  <TableCell header>Site</TableCell>
                  {isAdmin && (
                    <TableCell header align="center">
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.createdSites?.results?.map((createdSite) => (
                  <TableRow
                    key={`created-site-${createdSite.id}`}
                    onClick={() => navigate(`/customer/${createdSite.customerId}`)}
                  >
                    <TableCell>{formatDisplayDate(createdSite.createdAt)}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1.5">
                        {createdSite.isPrivate && (
                          <Lock
                            size={13}
                            className="text-red-600 flex-shrink-0"
                            aria-label="Réservé aux administrateurs"
                          />
                        )}
                        {createdSite.customer?.name ?? "-"}
                      </span>
                    </TableCell>
                    <TableCell>{createdSite.theme?.name ?? "-"}</TableCell>
                    <TableCell align="center">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                          STATUS_STYLES[createdSite.status]
                        }`}
                      >
                        {createdSiteStatusLabel(createdSite.status)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {createdSite.status === CreatedSiteStatus.Done && createdSite.url ? (
                        <a
                          href={
                            createdSite.url.startsWith("http")
                              ? createdSite.url
                              : `https://${createdSite.url}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-indigo-600 hover:underline"
                        >
                          <ExternalLink size={13} />
                          {createdSite.url}
                        </a>
                      ) : (
                        <span className="text-gray-400">
                          {createdSite.creationPercentage}%
                        </span>
                      )}
                    </TableCell>
                    {isAdmin && (
                      <TableCell align="center">
                        <button
                          type="button"
                          onClick={(e) => handleRemove(e, createdSite.id)}
                          title="Supprimer ce site"
                          className="text-gray-400 hover:text-red-600"
                        >
                          <Trash2 size={14} />
                        </button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </>
  );
};
